import React, { useEffect, useState, useCallback } from 'react';

// Collapsible sections outline for the clinic document view. Mirrors the
// "Back to patients" rail pattern: expanded → a slim panel listing every
// header-formatted line in the document (click to jump); collapsed → a thin
// vertical rail with an expand chevron so the document keeps its width.
//
// Sections are re-scanned from the live Quill instance on text-change
// (debounced) rather than persisted anywhere — the document itself is the
// source of truth, same as the old EditableDocumentPage sidebar.
// --- Header detection for templates that DON'T use Quill header formatting ---
// The scan below already lists every real header-blot line. These helpers add
// two more conventions so the outline works across every template (NP/NS,
// MEDSTUDENT_NP, plain-text notes) without anyone reformatting their headings:
//   1. a standalone ALL-CAPS line — "REVIEW OF SYSTEMS", "ORDERS", "HPI" — the
//      same rule the A&P / Master-Note parsers already use (icdProblemBlot.js,
//      DocumentBody.js), so the outline agrees with them.
//   2. an inline "Canonical Label:" line — "Past Medical History: ...",
//      "HPI: ..." — used by the NP/NS templates. Matched against a FIXED list
//      of real section names, so data fields ("TLC %:", "AHI:", "Bedtime:") and
//      prose ("the patient's review of systems was notable ...") never register.
// Detect-only: nothing here rewrites the note — it only populates the outline.
const SHORT_SECTIONS = new Set(['HPI', 'ROS', 'PE', 'PMH', 'PSH', 'FH', 'SH', 'CC', 'PLAN', 'LABS', 'MEDS', 'EXAM']);
const CANONICAL_SECTIONS = new Set([
  'chiefcomplaint', 'historyofpresentillness', 'hpi', 'intervalhistory',
  'reviewofsystems', 'ros', 'sleepros', 'sleepstudy',
  'pastmedicalhistory', 'pmh', 'pastsurgicalhistory', 'psh',
  'socialhistory', 'sh', 'familyhistory', 'fh',
  'allergies', 'medications', 'currentmedications', 'meds', 'inhalers',
  'vitals', 'vitalsigns', 'physicalexam', 'exam', 'pe',
  'subjective', 'objective', 'assessment', 'plan',
  'assessmentandplan', 'assessmentplan', 'ap',
  'labs', 'laboratory', 'imaging', 'results', 'diagnostics',
  'workup', 'pertinentlabs', 'pertinentlabsmicrobiology', 'labsmicrobiology', 'microbiology',
  'orders', 'impression', 'recommendations', 'procedures',
  'immunizations', 'vaccines', 'preventivecare', 'healthmaintenance', 'hospitalcourse',
]);
const normLabel = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
// Matches an already-detected header that IS the A&P, so we don't also synthesize one.
const AP_LABEL = /\bassessment\b|\bplan\b|\ba ?& ?p\b|\ba\/p\b/i;

// Returns { title, level } if the bare line reads as a section header, else null.
function detectHeaderLine(rawTitle) {
  const t = (rawTitle || '').trim();
  if (!t) return null;
  // 1. standalone ALL-CAPS line (drop a trailing colon first)
  const caps = t.replace(/:\s*$/, '').trim();
  if (caps.length <= 40 && /^[A-Z][A-Z0-9 &/()\-.]*$/.test(caps) &&
      !/\d{1,2}\/\d{1,2}/.test(caps) &&   // 'HST 6/9/26' is a dated sub-line, not a section
      (caps.length >= 5 || SHORT_SECTIONS.has(caps.replace(/\s+/g, '')))) {
    return { title: caps, level: 1 };
  }
  // 2. inline "Canonical Label:" — EXACT match of the text before the 1st colon
  const m = t.match(/^([^:\n]{1,40}):/);
  if (m && CANONICAL_SECTIONS.has(normLabel(m[1]))) {
    return { title: m[1].trim(), level: 1 };
  }
  // 3. standalone Title-Case section name, no colon — "Workup", "Assessment and
  //    Plan". Rule 1 only fires on ALL-CAPS and rule 2 needs a colon, so a
  //    template that writes a plain "Workup" heading matched nothing and the
  //    whole block (labs, imaging) went missing from the outline — Leejoe
  //    2026-07-16. The line must be the label AND NOTHING ELSE: the charset
  //    bars a leading bullet ("- Labs" is an order line, not a header) and bars
  //    the '.' that ends prose, so only a bare heading registers.
  if (/^[A-Za-z][A-Za-z0-9 &/()-]{0,39}$/.test(t) && CANONICAL_SECTIONS.has(normLabel(t))) {
    return { title: t, level: 1 };
  }
  return null;
}

export default function SectionsPanel({ quill, collapsed, onToggle }) {
  const [sections, setSections] = useState([]);

  const scan = useCallback(() => {
    if (!quill) { setSections([]); return; }
    const out = [];
    let sawAssessment = false;
    let apSynth = false;
    try {
      quill.getLines().forEach((line) => {
        const idx = quill.getIndex(line);
        const fmt = quill.getFormat(idx, 0) || {};
        const raw = (line.domNode?.innerText || '').trim();
        if (!raw) return;
        if (fmt.header) {
          out.push({ index: idx, title: raw, level: fmt.header });
          if (AP_LABEL.test(raw)) sawAssessment = true;
          return;
        }
        const detected = detectHeaderLine(raw);
        if (detected) {
          out.push({ index: idx, title: detected.title, level: detected.level });
          if (AP_LABEL.test(detected.title)) sawAssessment = true;
          return;
        }
        // NP/NS/FFS/FFP have NO 'Assessment & Plan' heading — the A&P is just
        // the '# problem' list. Surface it once, at the first problem, so the
        // outline isn't missing its most important section. Detect-only.
        if (!sawAssessment && !apSynth && /^#\s+\S/.test(raw)) {
          out.push({ index: idx, title: 'Assessment & Plan', level: 1 });
          apSynth = true;
        }
      });
    } catch (_) {}
    setSections(out);
  }, [quill]);

  useEffect(() => {
    if (!quill) { setSections([]); return undefined; }
    scan();
    let timer = null;
    const onChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(scan, 400);
    };
    quill.on('text-change', onChange);
    return () => {
      if (timer) clearTimeout(timer);
      quill.off('text-change', onChange);
    };
  }, [quill, scan]);

  const jump = (index) => {
    if (!quill) return;
    try {
      quill.setSelection(index, 0, 'user');
      const [line] = quill.getLine(index);
      line?.domNode?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {}
  };

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onToggle}
        aria-label="Show sections"
        title="Show sections"
        className="group w-8 shrink-0 self-stretch border-r border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] hover:bg-slate-50 dark:hover:bg-slate-800/40 flex flex-col items-center pt-3 gap-2 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors rounded-l-md"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
        <span
          className="text-[10px] font-bold tracking-[0.2em] uppercase"
          style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
        >
          Sections
        </span>
      </button>
    );
  }

  return (
    <aside className="w-52 shrink-0 self-stretch border-r border-slate-200 dark:border-slate-800/60 bg-white dark:bg-[#0a0a0f] flex flex-col min-h-0 rounded-l-md">
      <div className="flex items-center justify-between px-3 pt-3 pb-2 shrink-0">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 dark:text-slate-500">
          Sections
        </span>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Hide sections"
          title="Hide sections"
          className="text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-1 pb-3">
        {sections.length === 0 ? (
          <p className="px-2 pt-1 text-xs text-slate-400 dark:text-slate-500">
            No headers in this document yet.
          </p>
        ) : (
          sections.map((s, i) => (
            <button
              key={`${s.index}-${i}`}
              type="button"
              onClick={() => jump(s.index)}
              title={s.title}
              className="block w-full text-left truncate rounded px-2 py-1.5 text-xs text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors"
              style={{ paddingLeft: `${8 + (Math.min(s.level, 4) - 1) * 10}px` }}
            >
              {s.title}
            </button>
          ))
        )}
      </div>
    </aside>
  );
}
